import React, { useRef, useState, useEffect } from 'react';
import { LuX, LuFileText } from 'react-icons/lu';

// Formats an ISO timestamp as "Saved Mar 12, 2024" for the preloaded-resume banner.
const formatSavedAt = (isoString) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  return `Saved ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const ResumeInput = ({
  resume,
  fileName,
  error,
  isPreloaded,
  savedAt,
  savedResumes = [],
  onFileUpload,
  onRemoveFile,
  onTextChange,
  onApplyResume,
  onSaveResume,
}) => {
  const updateInputRef = useRef(null);

  // Saved resumes dropdown
  const [selectedResumeId, setSelectedResumeId] = useState('');

  // Save-to-library section
  const [wantToSave, setWantToSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved'

  // Default dropdown selection when list loads or changes
  useEffect(() => {
    if (savedResumes.length > 0 && !selectedResumeId) {
      setSelectedResumeId(savedResumes[0].id);
    }
  }, [savedResumes]); // eslint-disable-line

  // Reset save section whenever resume content changes
  useEffect(() => {
    setSaveStatus(null);
    setWantToSave(false);
  }, [resume]);

  const handleApply = () => {
    const found = savedResumes.find(r => r.id === selectedResumeId);
    if (found) onApplyResume(found.content);
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaveStatus('saving');
    try {
      await onSaveResume(resume, saveName.trim());
      setSaveStatus('saved');
      setSaveName('');
      setWantToSave(false);
      setTimeout(() => setSaveStatus(null), 2000);
    } catch {
      setSaveStatus(null);
    }
  };

  return (
    <div>

      {/* ── Saved resumes row (above everything, when library has entries) ── */}
      {savedResumes.length > 0 && (
        <div className="resume-saved-row">
          <span className="resume-saved-label">Saved resumes</span>
          <select
            className="resume-saved-select"
            value={selectedResumeId}
            onChange={e => setSelectedResumeId(e.target.value)}
          >
            {savedResumes.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="resume-apply-btn"
            onClick={handleApply}
            disabled={!selectedResumeId}
          >
            Apply
          </button>
        </div>
      )}

      {/* ── Main content area ── */}
      {isPreloaded && fileName ? (
        /* Pre-loaded from Supabase */
        <>
          <div className="resume-preloaded-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <LuFileText style={{ width: '16px', height: '16px', color: '#2E6AFF', flexShrink: 0 }} />
              <div>
                <span className="resume-preloaded-label">Resume on file</span>
                <span className="resume-preloaded-filename">{fileName}</span>
                {formatSavedAt(savedAt) && (
                  <span className="resume-preloaded-savedat"> · {formatSavedAt(savedAt)}</span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => updateInputRef.current?.click()}
                className="resume-update-btn"
              >
                Update
              </button>
              <button
                type="button"
                onClick={onRemoveFile}
                className="file-pill-remove"
                title="Remove saved resume"
              >
                <LuX style={{ width: '14px', height: '14px' }} />
              </button>
            </div>
            <input
              ref={updateInputRef}
              type="file"
              accept=".txt,.md,.pdf,.docx"
              onChange={onFileUpload}
              style={{ display: 'none' }}
            />
          </div>
        </>
      ) : !fileName ? (
        /* No file: textarea + upload zone + save section */
        <>
          <textarea
            className="input"
            rows="9"
            style={{ minHeight: '200px', resize: 'vertical' }}
            maxLength="100000"
            placeholder="Paste your full resume text here..."
            value={resume}
            onChange={(e) => onTextChange(e.target.value)}
          />
          {resume.length > 0 && (
            <div className={`resume-char-count${resume.length > 80000 ? ' warn' : ''}`}>
              {resume.length.toLocaleString()} / 100,000 characters
            </div>
          )}

          <div className="resume-divider">or</div>

          <label className="upload-zone">
            <div className="upload-zone-text">
              Drop resume here or <span className="upload-link">click to upload</span>
            </div>
            <div className="upload-zone-hint">TXT &middot; PDF &middot; DOCX</div>
            <input
              type="file"
              accept=".txt,.md,.pdf,.docx"
              onChange={onFileUpload}
              style={{ display: 'none' }}
            />
          </label>

          {/* ── Save to library section ── */}
          {resume.length > 0 && saveStatus !== 'saved' && (
            <div className="resume-save-section">
              <label className="resume-save-check-row">
                <input
                  type="checkbox"
                  checked={wantToSave}
                  onChange={e => setWantToSave(e.target.checked)}
                  className="resume-save-checkbox"
                />
                <span className="resume-save-check-label">Save this resume</span>
              </label>
              {wantToSave && (
                <div className="resume-save-name-row">
                  <input
                    type="text"
                    className="resume-save-name-input"
                    placeholder="e.g. Tech roles, Executive"
                    value={saveName}
                    onChange={e => setSaveName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSave()}
                    maxLength={80}
                  />
                  <button
                    type="button"
                    className="resume-save-btn"
                    onClick={handleSave}
                    disabled={!saveName.trim() || saveStatus === 'saving'}
                  >
                    {saveStatus === 'saving' ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          )}
          {saveStatus === 'saved' && (
            <div className="resume-save-confirmation">✓ Saved to your library</div>
          )}
        </>
      ) : (
        /* File uploaded this session */
        <div className="file-pill">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <LuFileText style={{ width: '18px', height: '18px', color: '#2E6AFF', flexShrink: 0 }} />
            <span className="file-pill-name">{fileName}</span>
          </div>
          <button
            type="button"
            onClick={onRemoveFile}
            className="file-pill-remove"
            title="Remove file"
          >
            <LuX style={{ width: '14px', height: '14px' }} />
          </button>
        </div>
      )}

      {error && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default ResumeInput;
